import ExamMeetingControls from '../exam-meetings/ExamMeetingControls';
import useVoiceActivity from '../../hooks/useVoiceActivity';
import { flushClassBoards } from '../../utils/classBoardPersistence';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleScreenSharing, toggleHandRaised,
  toggleMeetInfo, toggleChatStatus, toggleParticipantsStatus, toggleRecording
} from '../../store/slices/mediaSlice';
import {
  FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaDesktop,
  FaHandPaper, FaPhoneSlash, FaEllipsisH, FaComments, FaUsers,
  FaClosedCaptioning, FaInfoCircle, FaCircle, FaRegGrinAlt, FaVolumeUp,
  FaFileSignature, FaUserTie, FaTimes, FaPlus, FaSave, FaTrash, FaEdit, FaCheck,
  FaSpinner, FaChalkboardTeacher, FaBible, FaSignal, FaArrowsAlt,
  FaCompress, FaExpand, FaWindowMinimize, FaStop
} from 'react-icons/fa';
import { showConfirm } from '../../store/slices/uiSlice';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { supportsScreenSharing } from '../../utils/device';
import { motion, AnimatePresence } from 'framer-motion';
import {
  clearMeetingRuntimeStorage,
  getStoredMeetingSessionId,
  getStoredMeetingUserId,
} from '../../utils/meetingAuth';
import { store } from '../../store/store';
import api from '../../utils/api';
import meetingRecorder from '../../utils/meetingRecorder';
import {
  createMeetingNote,
  deleteMeetingNote,
  getMeetingNoteError,
  listMeetingNotes,
  updateMeetingNote,
} from '../../utils/meetingNotes';
import {
  clampMeetingNotesWindowPosition,
  clampMeetingNotesWindowSize,
  getDefaultMeetingNotesWindowPosition,
} from '../../utils/meetingNotesWindow';
import {
  clampMicGain,
  getStoredMeetingAudioMode,
  getStoredMicGain,
  MEETING_AUDIO_MODES,
  notifyMeetingInputDeviceChange,
  persistMeetingAudioMode,
  scanMeetingMediaDevices,
  supportsAudioOutputChooser,
  supportsAudioOutputSelection,
  supportsMicrophoneGain,
  verifyAudioOutputDevice,
} from '../../utils/meetingAudio';
import {
  getStoredVideoQualityMode,
  VIDEO_QUALITY_OPTIONS,
} from '../../utils/videoQuality';
import { canParticipantEndMeeting } from '../../utils/meetingAuthorization';
import { canUseRecordingControls, recordingUiError, stopMeetingRecording } from '../../utils/recordingControls';
import { pendingDirectRecordings, resumeDirectRecording } from '../../utils/directRecordingUpload';

const getNativeMeetingAudioOutputs = () => {
  try {
    const bridge = window.ShepNative;
    if (typeof bridge?.getMeetingAudioDevices !== 'function') return [];
    const value = bridge.getMeetingAudioDevices();
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((device) =>
        String(device?.deviceId || '').startsWith('native:')
      )
      .map((device) => ({
        deviceId: String(device.deviceId),
        label: String(device.label || 'Android audio device'),
        kind: 'audiooutput',
        native: true,
      }));
  } catch (error) {
    console.warn('Unable to read Android meeting audio devices:', error);
    return [];
  }
};

// ==================== SLIDER COMPONENT ====================
function Slider({ label, value, min, max, step, onChange, format = v => v }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[12px] text-gray-300">{label}</span>
        <span className="text-[12px] px-1.5 py-0.5 rounded-md bg-white/10 text-gray-100">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="pro-slider w-full h-2 appearance-none rounded-full bg-white/10"
        style={{
          backgroundImage: `linear-gradient(currentColor, currentColor), linear-gradient(#ffffff22,#ffffff22)`,
          backgroundSize: `${pct}% 100%, 100% 100%`,
          backgroundRepeat: 'no-repeat',
          color: '#60a5fa'
        }}
      />
    </div>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center rounded-full px-1 h-7 transition ${checked ? 'bg-emerald-500/80' : 'bg-white/15'}`}
    >
      <span className={`w-5 h-5 rounded-full bg-white shadow transform transition ${checked ? 'translate-x-6' : 'translate-x-0'}`} />
      <span className="ml-2 pr-2 text-[12px] text-gray-100">{label}</span>
    </button>
  );
}

// ==================== PROFESSIONAL MIXER ====================
function ProfessionalMixer() {
  const [micGain, setMicGain] = React.useState(1.0);
  const [gate, setGate] = React.useState(-55);
  const [eq, setEq] = React.useState([0, 0, 0, 0, 0]);
  const [noiseCut, setNoiseCut] = React.useState(true);

  const applySettings = React.useCallback(() => {
    const handler = window.mediaSoupHandler;
    if (handler) {
      if (handler.setMicGain) handler.setMicGain(micGain);
      if (handler.setNoiseGate) handler.setNoiseGate(gate);
      if (handler.toggleNoiseCut) handler.toggleNoiseCut(noiseCut);
      if (handler.setEqBand) {
        eq.forEach((val, idx) => handler.setEqBand(idx, val));
      }
    }
  }, [micGain, gate, noiseCut, eq]);

  React.useEffect(() => {
    applySettings();
    const interval = setInterval(applySettings, 1000);
    return () => clearInterval(interval);
  }, [applySettings]);

  const handleEqChange = (idx, val) => {
    const newEq = [...eq];
    newEq[idx] = Number(val);
    setEq(newEq);
  };

  const eqLabels = ["60Hz", "230Hz", "910Hz", "3.6kHz", "14kHz"];

  return (
    <div className="rounded-2xl bg-[#121417]/95 border border-white/10 backdrop-blur-xl p-4 md:p-5 shadow-2xl">
      <div className="mb-6">
        <h4 className="text-xs font-semibold text-gray-400 mb-4 uppercase tracking-wider">5-Band Equalizer</h4>
        <div className="flex justify-between items-center gap-2 h-40">
          {eq.map((val, idx) => (
            <div key={idx} className="flex flex-col items-center h-full w-10">
              <span className="text-[10px] text-gray-300 font-mono mb-2">{val > 0 ? `+${val}` : val}</span>
              <input
                type="range" min="-12" max="12" step="1" value={val}
                onChange={(e) => handleEqChange(idx, e.target.value)}
                className="w-1.5 h-24 appearance-none rounded-full bg-white/20 outline-none cursor-pointer"
                style={{ writingMode: 'bt-lr', WebkitAppearance: 'slider-vertical' }}
              />
              <span className="text-[10px] text-gray-400 mt-2 font-mono">{eqLabels[idx]}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="rounded-xl bg-white/5 p-3">
          <Slider label="Master Mic Gain" value={micGain} min={0} max={3.0} step={0.1} onChange={setMicGain} format={(v) => `${v.toFixed(1)}x`} />
        </div>
        <div className="rounded-xl bg-white/5 p-3">
          <Slider label="Noise Gate (Threshold)" value={gate} min={-90} max={-20} step={1} onChange={setGate} format={(v) => `${v} dB`} />
        </div>
      </div>

      <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
        <div className="flex flex-col">
          <span className="text-[12px] text-gray-200">Hum Notch Filter</span>
          <span className="text-[10px] text-gray-500">Cuts AC electrical hum (50/60Hz)</span>
        </div>
        <Switch checked={noiseCut} onChange={setNoiseCut} label={noiseCut ? 'ACTIVE' : 'BYPASS'} />
      </div>
    </div>
  );
}

// ==================== PERSISTED MEETING NOTES MODAL ====================
function StylishNotesModal({
  isOpen,
  onClose,
  roomId,
  roomTitle,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({ title: '', subtitle: '', content: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [windowMode, setWindowMode] = useState('normal');
  const [windowPosition, setWindowPosition] = useState(null);
  const [windowSize, setWindowSize] = useState(null);
  const windowRef = useRef(null);
  const windowDragRef = useRef(null);
  const windowResizeRef = useRef(null);
  const modeBeforeMinimizeRef = useRef('normal');

  const getViewport = useCallback(() => ({
    viewportWidth: window.visualViewport?.width || window.innerWidth,
    viewportHeight: window.visualViewport?.height || window.innerHeight,
  }), []);

  const constrainWindowPosition = useCallback((left, top) => {
    const notesWindow = windowRef.current;
    const width = notesWindow?.offsetWidth || Math.min(460, window.innerWidth - 16);
    const height = notesWindow?.offsetHeight || Math.min(660, window.innerHeight * 0.72);
    const styles = notesWindow ? window.getComputedStyle(notesWindow) : null;
    const controlsHeight = Number.parseFloat(
      styles?.getPropertyValue('--meeting-controls-height') || '92'
    ) || 92;

    return clampMeetingNotesWindowPosition({
      left,
      top,
      width,
      height,
      ...getViewport(),
      bottomInset: controlsHeight + 8,
    });
  }, [getViewport]);

  const beginWindowDrag = (event) => {
    if (
      windowMode === 'maximized' ||
      event.button !== 0 ||
      event.target?.closest?.('button, input, textarea, select, a') ||
      !windowRef.current
    ) return;

    const rect = windowRef.current.getBoundingClientRect();
    windowDragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setWindowPosition(constrainWindowPosition(rect.left, rect.top));
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    event.preventDefault();
  };

  const moveWindow = (event) => {
    const drag = windowDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setWindowPosition(constrainWindowPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY
    ));
    event.preventDefault();
  };

  const endWindowDrag = (event) => {
    const drag = windowDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    windowDragRef.current = null;
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch {}
  };

  const beginWindowResize = (event) => {
    if (
      windowMode !== 'normal' ||
      event.button !== 0 ||
      !windowRef.current
    ) return;

    const rect = windowRef.current.getBoundingClientRect();
    const styles = window.getComputedStyle(windowRef.current);
    const controlsHeight = Number.parseFloat(
      styles.getPropertyValue('--meeting-controls-height') || '92'
    ) || 92;
    windowResizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      left: rect.left,
      top: rect.top,
      bottomInset: controlsHeight + 8,
    };
    setWindowPosition(constrainWindowPosition(rect.left, rect.top));
    setWindowSize({ width: rect.width, height: rect.height });
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    event.stopPropagation();
    event.preventDefault();
  };

  const resizeWindow = (event) => {
    const resize = windowResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    setWindowSize(clampMeetingNotesWindowSize({
      width: resize.width + event.clientX - resize.startX,
      height: resize.height + event.clientY - resize.startY,
      left: resize.left,
      top: resize.top,
      ...getViewport(),
      bottomInset: resize.bottomInset,
    }));
    event.stopPropagation();
    event.preventDefault();
  };

  const endWindowResize = (event) => {
    const resize = windowResizeRef.current;
    if (!resize || resize.pointerId !== event.pointerId) return;
    windowResizeRef.current = null;
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch {}
    event.stopPropagation();
  };

  const minimizeWindow = () => {
    modeBeforeMinimizeRef.current = windowMode === 'maximized'
      ? 'maximized'
      : 'normal';
    setWindowMode('minimized');
  };

  const restoreMinimizedWindow = () => {
    setWindowMode(modeBeforeMinimizeRef.current || 'normal');
  };

  const toggleMaximizedWindow = () => {
    setWindowMode((mode) => mode === 'maximized' ? 'normal' : 'maximized');
  };

  const closeWindow = () => {
    windowDragRef.current = null;
    windowResizeRef.current = null;
    setWindowMode('normal');
    setDeleteConfirm(null);
    onClose();
  };

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const nextNotes = await listMeetingNotes({ roomId });
      setNotes(nextNotes);
    } catch (error) {
      const message = getMeetingNoteError(
        error,
        'Unable to load your meeting notes.'
      );
      setLoadError(message);
      toast.error(message, { toastId: `meeting-notes-load-${roomId}` });
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  const updateForm = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
    setSaveStatus('dirty');
  };

  const saveNote = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Please enter content');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    try {
      const payload = {
        ...formData,
        roomId,
        roomTitle,
      };
      const savedNote = editingNote
        ? await updateMeetingNote(editingNote._id, payload)
        : await createMeetingNote(payload);
      if (!savedNote?._id) {
        throw new Error('The server did not return the saved note.');
      }

      setNotes((previous) => [
        savedNote,
        ...previous.filter((note) => note._id !== savedNote._id),
      ]);
      setEditingNote(savedNote);
      setFormData({
        title: savedNote.title || '',
        subtitle: savedNote.subtitle || '',
        content: savedNote.content || '',
      });
      setSaveStatus('saved');
      toast.success(editingNote ? 'Note updated.' : 'Note saved.');
    } catch (error) {
      const message = getMeetingNoteError(error, 'Unable to save this note.');
      setSaveStatus('error');
      toast.error(message, { toastId: 'meeting-note-save-error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    setDeletingId(noteId);
    try {
      await deleteMeetingNote(noteId);
      setNotes((previous) => previous.filter((note) => note._id !== noteId));
      setDeleteConfirm(null);
      if (editingNote?._id === noteId) {
        setFormData({ title: '', subtitle: '', content: '' });
        setEditingNote(null);
        setSaveStatus('idle');
      }
      toast.success('Note deleted.');
    } catch (error) {
      toast.error(
        getMeetingNoteError(error, 'Unable to delete this note.'),
        { toastId: 'meeting-note-delete-error' }
      );
    } finally {
      setDeletingId('');
    }
  };

  const editNote = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title || '',
      subtitle: note.subtitle || '',
      content: note.content || ''
    });
    setSaveStatus('idle');
  };

  const newNote = () => {
    setEditingNote(null);
    setFormData({ title: '', subtitle: '', content: '' });
    setSaveStatus('idle');
  };

  useEffect(() => {
    if (isOpen && roomId) void fetchNotes();
  }, [fetchNotes, isOpen, roomId]);

  useEffect(() => {
    if (!isOpen || windowMode === 'maximized') return undefined;

    const reposition = () => {
      const notesWindow = windowRef.current;
      if (!notesWindow) return;
      const styles = window.getComputedStyle(notesWindow);
      const controlsHeight = Number.parseFloat(
        styles.getPropertyValue('--meeting-controls-height') || '92'
      ) || 92;
      const dimensions = {
        width: notesWindow.offsetWidth,
        height: notesWindow.offsetHeight,
        ...getViewport(),
        bottomInset: controlsHeight + 8,
      };

      setWindowPosition((position) => position
        ? constrainWindowPosition(position.left, position.top)
        : getDefaultMeetingNotesWindowPosition(dimensions));
    };

    const animationFrame = window.requestAnimationFrame(reposition);
    window.addEventListener('resize', reposition);
    window.addEventListener('orientationchange', reposition);
    window.visualViewport?.addEventListener('resize', reposition);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('orientationchange', reposition);
      window.visualViewport?.removeEventListener('resize', reposition);
    };
  }, [constrainWindowPosition, getViewport, isOpen, windowMode]);

  if (!isOpen) return null;

  const isMaximized = windowMode === 'maximized';
  const isMinimized = windowMode === 'minimized';
  const windowStyle = isMaximized
    ? {
        left: 8,
        right: 8,
        top: 8,
        bottom: 'calc(var(--meeting-controls-height, 5.75rem) + 8px)',
      }
    : {
        left: windowPosition?.left ?? 8,
        top: windowPosition?.top ?? 64,
        width: isMinimized
          ? 'min(320px, calc(100vw - 16px))'
          : windowSize?.width ?? 'min(460px, calc(100vw - 16px))',
        ...(isMinimized ? {} : {
          height: windowSize?.height ?? 'min(72dvh, 660px, calc(100dvh - var(--meeting-controls-height, 5.75rem) - 16px))',
          minWidth: 'min(300px, calc(100vw - 16px))',
          minHeight: 'min(280px, calc(100dvh - var(--meeting-controls-height, 5.75rem) - 16px))',
          maxWidth: 'calc(100vw - 16px)',
          maxHeight: 'calc(100dvh - var(--meeting-controls-height, 5.75rem) - 16px)',
        }),
      };

  return (
    <>
      <motion.section
        ref={windowRef}
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -12 }}
        transition={{ duration: 0.18 }}
        className="fixed z-[240] flex max-h-[calc(100dvh-16px)] flex-col overflow-hidden rounded-2xl border border-blue-300/30 bg-[#0f1115]/95 text-white shadow-[0_20px_70px_rgba(0,0,0,0.55)] backdrop-blur-xl"
        style={windowStyle}
        role="dialog"
        aria-label="Meeting notes window"
        data-meeting-notes-window="true"
        data-window-mode={windowMode}
      >
        <header
          className={`flex shrink-0 touch-none select-none items-center gap-2 border-b border-white/10 bg-slate-900/95 px-2.5 py-2 ${isMaximized ? '' : 'cursor-move'}`}
          onPointerDown={beginWindowDrag}
          onPointerMove={moveWindow}
          onPointerUp={endWindowDrag}
          onPointerCancel={endWindowDrag}
          onLostPointerCapture={endWindowDrag}
          onDoubleClick={(event) => {
            if (!event.target?.closest?.('button')) toggleMaximizedWindow();
          }}
          aria-label={isMaximized ? 'Meeting notes title bar' : 'Drag meeting notes window'}
          title={isMaximized ? 'Double-click to restore' : 'Drag to move · double-click to maximize'}
        >
          <FaArrowsAlt className="shrink-0 text-blue-300" size={13} aria-hidden="true" />
          <FaFileSignature className="shrink-0 text-blue-400" aria-hidden="true" />
          <strong className="min-w-0 flex-1 truncate text-sm">Meeting Notes</strong>
          <span className="hidden text-[9px] uppercase tracking-wider text-slate-400 sm:inline">
            {isMinimized ? 'Minimized' : isMaximized ? 'Maximized' : 'Floating'}
          </span>

          {isMinimized ? (
            <button
              type="button"
              onClick={restoreMinimizedWindow}
              className="inline-flex items-center gap-1 rounded-md bg-blue-500/20 px-2 py-1.5 text-[11px] font-semibold text-blue-100 hover:bg-blue-500/35"
              title="Restore notes window"
              aria-label="Restore notes window"
            >
              <FaExpand size={10} /> Restore
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={minimizeWindow}
                className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
                title="Minimize notes window"
                aria-label="Minimize notes window"
              >
                <FaWindowMinimize size={12} />
              </button>
              <button
                type="button"
                onClick={toggleMaximizedWindow}
                className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
                title={isMaximized ? 'Restore notes window size' : 'Maximize notes window'}
                aria-label={isMaximized ? 'Restore notes window size' : 'Maximize notes window'}
              >
                {isMaximized ? <FaCompress size={12} /> : <FaExpand size={12} />}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={closeWindow}
            className="rounded-md p-1.5 text-slate-300 hover:bg-red-500/20 hover:text-red-200"
            title="Close notes window"
            aria-label="Close notes window"
          >
            <FaTimes size={13} />
          </button>
        </header>

        {!isMinimized && (
          <div className={`flex min-h-0 flex-1 ${isMaximized ? 'flex-col md:flex-row' : 'flex-col'}`}>
            {/* SAVED NOTES */}
            <div className={`flex w-full flex-col border-b border-white/10 bg-[#0a0c10] ${isMaximized ? 'max-h-[34%] min-h-[120px] md:max-h-none md:w-[30%] md:border-b-0 md:border-r' : 'max-h-[34%] min-h-[124px]'}`}>
              <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-300">
                  Saved notes
                </h3>
                <button
                  type="button"
                  onClick={newNote}
                  className="flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs text-white transition hover:bg-blue-500"
                >
                  <FaPlus size={10} /> New
                </button>
              </div>

              <div className="meeting-participant-scroll min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2">
                {loading && notes.length === 0 ? (
                  <div className="flex justify-center py-7">
                    <FaSpinner className="animate-spin text-xl text-white/40" />
                  </div>
                ) : loadError ? (
                  <div className="px-3 py-5 text-center text-xs text-red-300">
                    <p>{loadError}</p>
                    <button
                      type="button"
                      onClick={() => void fetchNotes()}
                      className="mt-2 rounded-lg bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
                    >
                      Try again
                    </button>
                  </div>
                ) : notes.length === 0 ? (
                  <div className="py-6 text-center text-xs text-gray-500">
                    No notes yet.<br />Click "New" to create one.
                  </div>
                ) : (
                  notes.map((note) => (
                    <div
                      key={note._id}
                      onClick={() => editNote(note)}
                      className={`group cursor-pointer rounded-xl border-l-4 p-2.5 transition-all duration-200 ${
                        editingNote?._id === note._id
                          ? 'border-blue-500 bg-blue-600/30'
                          : 'border-transparent bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate text-xs font-medium text-white">{note.title || 'Untitled'}</h4>
                          {note.subtitle && (
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">{note.subtitle}</p>
                          )}
                          <p className="mt-1 text-[9px] text-gray-500">
                            {new Date(note.updatedAt || note.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeleteConfirm(note._id);
                          }}
                          disabled={deletingId === note._id}
                          className="p-1 text-gray-500 opacity-70 transition hover:text-red-400 focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                          title="Delete note"
                          aria-label={`Delete ${note.title || 'untitled note'}`}
                        >
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* NOTE EDITOR */}
            <div className={`flex min-h-0 w-full flex-1 flex-col bg-[#0f1115] ${isMaximized ? 'md:w-[70%]' : ''}`}>
              <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
                <h3 className="truncate text-sm font-semibold text-white">
                  {editingNote ? '✏️ Edit Note' : '📝 Create New Note'}
                </h3>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto p-3">
                <input
                  type="text"
                  placeholder="Note title"
                  value={formData.title}
                  onChange={(event) => updateForm('title', event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-medium text-white placeholder-gray-500 transition focus:border-blue-500 focus:outline-none"
                />

                <input
                  type="text"
                  placeholder="Subtitle / heading (optional)"
                  value={formData.subtitle}
                  onChange={(event) => updateForm('subtitle', event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-gray-500 transition focus:border-blue-500 focus:outline-none"
                />

                <textarea
                  placeholder="Write your meeting notes here...&#10;&#10;• Important points&#10;• Decisions made&#10;• Action items"
                  value={formData.content}
                  onChange={(event) => updateForm('content', event.target.value)}
                  className={`w-full resize-y rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-gray-500 transition focus:border-blue-500 focus:outline-none ${isMaximized ? 'h-64 min-h-[10rem]' : 'h-40 min-h-[8rem]'}`}
                />
              </div>

              <div className={`flex flex-wrap items-center justify-end gap-2 border-t border-white/10 p-3 ${isMaximized ? '' : 'pr-12'}`}>
                <span
                  aria-live="polite"
                  className={`mr-auto min-w-0 flex-1 text-[10px] ${
                    saveStatus === 'error'
                      ? 'text-red-300'
                      : saveStatus === 'saved'
                        ? 'text-emerald-300'
                        : 'text-gray-400'
                  }`}
                >
                  {saveStatus === 'saving' && 'Saving securely…'}
                  {saveStatus === 'saved' && 'Saved to your dashboard notes.'}
                  {saveStatus === 'dirty' && 'Unsaved changes'}
                  {saveStatus === 'error' && 'Not saved — please retry.'}
                </span>
                {editingNote && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingNote(null);
                      setFormData({ title: '', subtitle: '', content: '' });
                      setSaveStatus('idle');
                    }}
                    className="rounded-lg bg-white/10 px-3 py-2 text-xs text-gray-300 transition hover:bg-white/20"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={saveNote}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 px-3.5 py-2 text-xs font-medium text-white transition hover:from-blue-500 hover:to-blue-400 disabled:opacity-50"
                >
                  {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                  {editingNote ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {!isMinimized && !isMaximized && (
          <button
            type="button"
            className="absolute bottom-0 right-0 z-20 flex h-9 w-9 touch-none cursor-nwse-resize items-end justify-end rounded-tl-xl border-l border-t border-blue-300/25 bg-slate-800/95 p-1.5 text-base font-black leading-none text-blue-200 shadow-[-4px_-4px_14px_rgba(15,23,42,0.55)] hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-300"
            onPointerDown={beginWindowResize}
            onPointerMove={resizeWindow}
            onPointerUp={endWindowResize}
            onPointerCancel={endWindowResize}
            onLostPointerCapture={endWindowResize}
            aria-label="Resize meeting notes window"
            title="Drag to make notes window larger or smaller"
            data-meeting-notes-resize-handle="true"
          >
            <span aria-hidden="true">↘</span>
          </button>
        )}
      </motion.section>

      {/* DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {deleteConfirm && (
          <div className="meeting-dialog-overlay fixed inset-0 z-[250] flex items-center justify-center bg-black/80">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="meeting-dialog bg-[#1a1c22] rounded-xl p-6 max-w-sm w-full mx-4 border border-red-500/30"
            >
              <h3 className="text-white text-lg font-semibold mb-2">Delete Note?</h3>
              <p className="text-gray-400 text-sm mb-5">This action cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 rounded-lg bg-white/10 text-gray-300 hover:bg-white/20 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteNote(deleteConfirm)}
                  disabled={deletingId === deleteConfirm}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition flex items-center gap-2"
                >
                  {deletingId === deleteConfirm
                    ? <FaSpinner className="animate-spin" />
                    : <FaTrash size={12} />}
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

// ==================== INTERVIEW DECISION MODAL ====================
function InterviewDecisionModal({ isOpen, onClose, studentRegNo, studentName, onSubmit }) {
  const [decision, setDecision] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!decision) {
      toast.error('Please select a decision');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(decision);
      toast.success(`Applicant marked as ${decision.toUpperCase()}`);
      onClose();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="meeting-dialog-overlay fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="meeting-dialog bg-gradient-to-br from-[#1a1c24] to-[#0f1117] rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border border-white/10"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white text-xl font-bold flex items-center gap-2">
              <FaUserTie className="text-purple-400" />
              Interview Decision
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <FaTimes size={20} />
            </button>
          </div>

          <div className="mb-5">
            <p className="text-gray-300 text-sm mb-1">Applicant</p>
            <p className="text-white font-medium">{studentName}</p>
            <p className="text-gray-400 text-xs">Reg No: {studentRegNo}</p>
          </div>

          <div className="space-y-3 mb-6">
            <button
              onClick={() => setDecision('selected')}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                decision === 'selected'
                  ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                  : 'bg-white/10 text-gray-300 hover:bg-green-600/20 hover:text-green-400'
              }`}
            >
              {decision === 'selected' && <FaCheck size={16} />}
              ✅ SELECTED
            </button>
            
            <button
              onClick={() => setDecision('waitlist')}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                decision === 'waitlist'
                  ? 'bg-yellow-600 text-white shadow-lg shadow-yellow-600/30'
                  : 'bg-white/10 text-gray-300 hover:bg-yellow-600/20 hover:text-yellow-400'
              }`}
            >
              {decision === 'waitlist' && <FaCheck size={16} />}
              📋 WAITLIST
            </button>
            
            <button
              onClick={() => setDecision('rejected')}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                decision === 'rejected'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'bg-white/10 text-gray-300 hover:bg-red-600/20 hover:text-red-400'
              }`}
            >
              {decision === 'rejected' && <FaCheck size={16} />}
              ❌ REJECTED
            </button>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/10 text-gray-300 hover:bg-white/20 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !decision}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 text-white font-medium hover:from-purple-500 hover:to-purple-400 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <FaSpinner className="animate-spin" /> : 'Submit Decision'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const emitWithAck = (socket, eventName, payload, timeoutMs = 2500) =>
  new Promise((resolve) => {
    if (!socket?.connected) {
      resolve({ success: false, error: 'Socket is not connected.' });
      return;
    }

    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ success: false, error: 'Server acknowledgement timed out.' });
    }, timeoutMs);

    socket.emit(eventName, payload, (response = {}) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(response);
    });
  });

// ==================== MAIN CONTROLS COMPONENT ====================
const Controls = ({ remoteStreams, remoteVideoStatus,
  socket,
  onToggleChat,
  onToggleParticipants,
  onToggleBible,
  onToggleMeetingInfo,
  onOpenBlackboard,
  showMore,
  bibleOpen = false,
  unreadMessages = 0,
  hasUnreadChat = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showReactions, setShowReactions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingSavePhase, setRecordingSavePhase] = useState('');
  const [recordingSaveRetry, setRecordingSaveRetry] = useState(false);
  const recordingActionRef = useRef(null);
  const stoppedRecordingIdRef = useRef('');
  const [mediaToggleBusy, setMediaToggleBusy] = useState('');
  const { localStream, audioEnabled, videoEnabled, screenSharing, handRaised, recording, showInfoStatus, chatStatus, participantsStatus } = useSelector((state) => state.media);
  const currentRoom = useSelector((state) => state.room.currentRoom);
  const isExam = currentRoom?.meetingKind === 'exam';
  const examStream = useSelector(state => state.media.localStream);
  const participantStatus = useSelector((state) => state.room.localParticipantStatus);
  const statusget = typeof participantStatus === 'string'
    ? participantStatus
    : sessionStorage.getItem('statusget');
  const moreMenuRef = useRef();
  const authUser = useSelector((state) => state.auth.user);
  const effectiveRole = authUser?.role || sessionStorage.getItem('userRole') || '';
  const canEndMeeting = !isExam && canParticipantEndMeeting({
    access: currentRoom?.access,
    meetingType: currentRoom?.meetingType,
    role: effectiveRole,
  });
  const canRecord = canUseRecordingControls({ room: currentRoom, role: effectiveRole, participantStatus: statusget });
  const canOpenBlackboard =
    currentRoom?.meetingType === 'class' &&
    ['admitted', 'presenting'].includes(String(statusget));
  const canDecideInterview =
    currentRoom?.meetingType === 'interview' &&
    currentRoom?.access?.isPrimaryAdmin === true;

  const trackStudentControl = useCallback((event) => {
    if (!socket || String(authUser?.role || '').toLowerCase() !== 'student') return;
    const button = event.target?.closest?.('button');
    if (!button) return;
    const label = String(
      button.dataset?.control || button.getAttribute('aria-label') ||
      button.getAttribute('title') || button.innerText || 'button'
    ).trim().toLowerCase().replace(/\s+/g, '-').slice(0, 100);
    if (!label) return;
    socket.emit('attendance:control', {
      control: label,
      metadata: { disabled: Boolean(button.disabled), meetingType: currentRoom?.meetingType || '' },
    });
  }, [authUser?.role, currentRoom?.meetingType, socket]);

  const stopRoomRecording = async () => {
    if (recordingSaveRetry && !meetingRecorder.isOwnerForRoom(currentRoom?.roomId) && !meetingRecorder.isSavingForRoom?.(currentRoom?.roomId)) {
      const pending = await pendingDirectRecordings(currentRoom?.roomId);
      const saved = pending.find(item => item.id === stoppedRecordingIdRef.current);
      if (saved) {
        setRecordingSavePhase('saving');
        return resumeDirectRecording(saved.id);
      }
    }
    return stopMeetingRecording({
    roomId: currentRoom?.roomId, recorder: meetingRecorder, socket, api,
    onPhase: setRecordingSavePhase,
    recordingId: stoppedRecordingIdRef.current,
    onRecordingId: id => { stoppedRecordingIdRef.current = String(id); },
    requestStop: () => emitWithAck(socket, 'recording:request-stop', { roomId: currentRoom?.roomId,
      ...(recordingSaveRetry && stoppedRecordingIdRef.current ? { recordingId: stoppedRecordingIdRef.current } : {}) }, 5000),
    });
  };

  useEffect(() => {
    const requested = event => {
      if (String(event.detail?.roomId || '') === String(currentRoom?.roomId || '')) void recordingActionRef.current?.();
    };
    window.addEventListener('meeting-recording-stop-click', requested);
    return () => window.removeEventListener('meeting-recording-stop-click', requested);
  }, [currentRoom?.roomId]);

  useEffect(() => {
    setRecordingSavePhase(''); setRecordingSaveRetry(false);
    stoppedRecordingIdRef.current = '';
  }, [currentRoom?.roomId]);
  useEffect(() => {
    const saved = event => {
      const result = event.detail;
      if (result?.state !== 'completed' || result.roomId !== currentRoom?.roomId || result.recordingId !== stoppedRecordingIdRef.current) return;
      dispatch(toggleRecording(false));
      setRecordingSaveRetry(false); setRecordingSavePhase('');
      if (recordingSaveRetry) toast.success('Recording saved.');
    };
    window.addEventListener('meeting-recording-direct-status', saved);
    return () => window.removeEventListener('meeting-recording-direct-status', saved);
  }, [currentRoom?.roomId, dispatch, recordingSaveRetry]);
  const ua = navigator.userAgent;
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const canScreenShare = !isMobileUA || supportsScreenSharing();

  // ==================== INTERVIEW DECISION STATE ====================
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentApplicant, setCurrentApplicant] = useState({ regNo: '', name: '' });

  const reactions = [
    '👍', '👎', '👏', '😂', '😮', '❤️', '🙏', '🤲', '🙌', '🎉', '💯', '🔥', '✨', '✅', '❌', '🥰', '😊', '😢', '🤔', '🤗',
    'Amen', 'Praise the Lord', 'Hallelujah', 'Ok', 'Sorry', 'Sir', 'Good Morning', 'Good Afternoon', 'Good Night',
    'Good Evening', 'Hi', 'Hello', 'I Like', 'Thanks', 'Welcome', 'Bless You', 'God Bless', 'Take Care', 'See You',
    'Bye', 'Love You All', 'Thankyou', 'Jesus', 'Oh! my God'
  ];

  const MAX_BOOST = 2.0;
  const [loudOn, setLoudOn] = useState(() => {
    const cur = (typeof window !== 'undefined' && typeof window.__playbackBoost === 'number') ? window.__playbackBoost : 1.0;
    return cur > 1.01;
  });

  const setLoud = (on) => {
    setLoudOn(on);
    window.__playbackBoost = on ? MAX_BOOST : 1.0;
    window.__emitAudioSettingsChange?.();
  };

  const [isScreenShareLoading, setIsScreenShareLoading] = useState(false);
  const [showTray, setShowTray] = useState(false);
  const [showAudioPanel, setShowAudioPanel] = useState(false);
  const { level: microphoneLevel } = useVoiceActivity(
    showAudioPanel && audioEnabled ? localStream : null
  );
  const [showVideoQualityPanel, setShowVideoQualityPanel] = useState(false);
  const [videoQualityMode, setVideoQualityModeState] = useState(
    () => getStoredVideoQualityMode()
  );
  const [appliedVideoProfile, setAppliedVideoProfile] = useState('');
  const [videoQualityBusy, setVideoQualityBusy] = useState(false);
  const [videoQualityMessage, setVideoQualityMessage] = useState('');
  const [audioMode, setAudioModeState] = useState(
    () => getStoredMeetingAudioMode()
  );
  const [audioModeBusy, setAudioModeBusy] = useState(false);
  const [audioModeMessage, setAudioModeMessage] = useState('');
  const [mediaDevices, setMediaDevices] = useState({
    audioinput: [],
    videoinput: [],
    audiooutput: [],
  });
  const [selectedDevices, setSelectedDevices] = useState(() => ({
    audioinput: localStorage.getItem('meeting.audioInputId') || '',
    videoinput: localStorage.getItem('meeting.videoInputId') || '',
    audiooutput:
      localStorage.getItem('meeting.nativeAudioOutputId') ||
      localStorage.getItem('meeting.audioOutputId') ||
      '',
  }));
  const supportsOutputSelection = supportsAudioOutputSelection();
  const supportsOutputChooser = supportsAudioOutputChooser();
  const supportsNativeOutputSelection =
    typeof window.ShepNative?.setMeetingAudioDevice === 'function';
  const [deviceScanBusy, setDeviceScanBusy] = useState(false);
  const [speakerChoiceBusy, setSpeakerChoiceBusy] = useState(false);
  const [deviceSwitchBusy, setDeviceSwitchBusy] = useState('');
  const [deviceScanMessage, setDeviceScanMessage] = useState('');
  const mediaDeviceScanSequenceRef = useRef(0);
  const micGainSupported = supportsMicrophoneGain();
  const [micGain, setMicGainValue] = useState(() => getStoredMicGain());
  const [micGainError, setMicGainError] = useState('');

  const syncSelectedDevicesFromStorage = useCallback((nextDevices) => {
    setSelectedDevices((previous) => {
      const next = { ...previous };
      for (const [kind, storageKey] of [
        ['audioinput', 'meeting.audioInputId'],
        ['videoinput', 'meeting.videoInputId'],
        ['audiooutput', 'meeting.audioOutputId'],
      ]) {
        const storedDeviceId =
          kind === 'audiooutput'
            ? localStorage.getItem('meeting.nativeAudioOutputId') ||
              localStorage.getItem(storageKey) ||
              ''
            : localStorage.getItem(storageKey) || '';
        // Device labels/IDs may be privacy-redacted until permission is
        // granted, and Bluetooth profiles can disappear briefly while Linux
        // switches A2DP/HFP. Keep the preference visible until an actual
        // exact capture/output attempt proves it cannot be used.
        next[kind] = storedDeviceId;
      }
      return next;
    });
  }, []);

  const refreshMediaDevices = useCallback(async ({
    requestAudioPermission = false,
  } = {}) => {
    const requestSequence = ++mediaDeviceScanSequenceRef.current;
    setDeviceScanBusy(true);
    try {
      const currentAudioTracks =
        window.mediaSoupHandler?.localStream?.getAudioTracks?.() || [];
      const { devices, permissionError } = await scanMeetingMediaDevices({
        requestAudioPermission,
        hasLiveAudioTrack: currentAudioTracks.some(
          (track) => track?.readyState === 'live'
        ),
      });
      if (requestSequence !== mediaDeviceScanSequenceRef.current) {
        return devices;
      }

      const nativeOutputs = getNativeMeetingAudioOutputs();
      const nextDevices = {
        ...devices,
        audiooutput: [
          ...devices.audiooutput,
          ...nativeOutputs.filter(
            (nativeDevice) =>
              !devices.audiooutput.some(
                (webDevice) =>
                  String(webDevice.deviceId) === nativeDevice.deviceId
              )
          ),
        ],
      };
      setMediaDevices(nextDevices);
      syncSelectedDevicesFromStorage(nextDevices);

      const savedOutputId =
        localStorage.getItem('meeting.nativeAudioOutputId') ||
        localStorage.getItem('meeting.audioOutputId') ||
        '';
      const savedOutputAvailable =
        !savedOutputId ||
        nextDevices.audiooutput.some(
          (device) => String(device.deviceId) === savedOutputId
        );
      if (permissionError?.name === 'NotAllowedError') {
        setDeviceScanMessage(
          'Microphone access is blocked. Allow it in this site’s browser settings, then refresh devices.'
        );
      } else if (permissionError?.name === 'NotFoundError') {
        setDeviceScanMessage(
          'No microphone was detected. Connect the Bluetooth/USB device, then refresh devices.'
        );
      } else if (permissionError) {
        setDeviceScanMessage(
          permissionError.message || 'Audio-device permission could not be opened.'
        );
      } else if (!savedOutputAvailable) {
        setDeviceScanMessage(
          'The saved speaker is not available now, so meeting audio is using the system default.'
        );
      } else {
        const microphoneCount = nextDevices.audioinput.length;
        const speakerCount = nextDevices.audiooutput.length;
        setDeviceScanMessage(
          `Found ${microphoneCount} microphone${
            microphoneCount === 1 ? '' : 's'
          } and ${speakerCount} speaker${
            speakerCount === 1 ? '' : 's'
          }.`
        );
      }
      return nextDevices;
    } catch (error) {
      if (requestSequence !== mediaDeviceScanSequenceRef.current) return null;
      console.warn('Unable to enumerate meeting devices:', error);
      setDeviceScanMessage(
        error?.message || 'Unable to refresh meeting devices.'
      );
      return null;
    } finally {
      if (requestSequence === mediaDeviceScanSequenceRef.current) {
        setDeviceScanBusy(false);
      }
    }
  }, [syncSelectedDevicesFromStorage]);

  useEffect(() => {
    if (!showAudioPanel) return undefined;
    let settleTimer = null;
    let finalTimer = null;
    const onDeviceChange = () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      if (finalTimer) window.clearTimeout(finalTimer);
      settleTimer = window.setTimeout(() => {
        void refreshMediaDevices();
      }, 350);
      // Bluetooth profile changes can expose the microphone and speaker in
      // separate PipeWire/PulseAudio updates. The trailing refresh wins.
      finalTimer = window.setTimeout(() => {
        void refreshMediaDevices();
      }, 1200);
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      if (finalTimer) window.clearTimeout(finalTimer);
      navigator.mediaDevices?.removeEventListener?.(
        'devicechange',
        onDeviceChange
      );
    };
  }, [refreshMediaDevices, showAudioPanel]);

  const selectMediaDevice = async (kind, deviceId) => {
    if (deviceSwitchBusy) return;
    setDeviceSwitchBusy(kind);
    const storageKey =
      kind === 'audioinput'
        ? 'meeting.audioInputId'
        : kind === 'videoinput'
          ? 'meeting.videoInputId'
          : 'meeting.audioOutputId';
    const previousDeviceId =
      kind === 'audiooutput'
        ? localStorage.getItem('meeting.nativeAudioOutputId') ||
          localStorage.getItem(storageKey) ||
          ''
        : localStorage.getItem(storageKey) || '';
    const persistSelection = (nextDeviceId) => {
      if (nextDeviceId) localStorage.setItem(storageKey, nextDeviceId);
      else localStorage.removeItem(storageKey);
      setSelectedDevices((previous) => ({
        ...previous,
        [kind]: nextDeviceId,
      }));
    };

    try {
    if (
      kind === 'audiooutput' &&
      supportsNativeOutputSelection &&
      (String(deviceId).startsWith('native:') || !supportsOutputSelection)
    ) {
      const routed = window.ShepNative.setMeetingAudioDevice(
        String(deviceId || '')
      );
      if (routed !== true && routed !== 'true') {
        throw new Error('Android could not route audio to that device.');
      }
      if (deviceId) {
        localStorage.setItem('meeting.nativeAudioOutputId', deviceId);
      } else {
        localStorage.removeItem('meeting.nativeAudioOutputId');
      }
      localStorage.removeItem('meeting.audioOutputId');
      setSelectedDevices((previous) => ({
        ...previous,
        audiooutput: deviceId,
      }));
      window.dispatchEvent(
        new CustomEvent('meeting-output-device-change', {
          detail: { deviceId: '' },
        })
      );
      toast.success('Android meeting audio route changed.');
      return;
    }
    if (kind !== 'audiooutput') {
      const handlerKind = kind === 'audioinput' ? 'audio' : 'video';

      const switchInput = window.mediaSoupHandler?.switchInputDevice;
      if (typeof switchInput !== 'function') {
        // Before admission there is no live producer to replace. Saving is
        // intentional here; the selected device is used on first media start.
        persistSelection(deviceId);
        if (kind === 'audioinput') notifyMeetingInputDeviceChange({
          deviceId, reason: 'saved-before-admission', enabled: false,
        });
        toast.info('Device saved and will be used when meeting media starts.');
        return;
      }

      try {
        await switchInput.call(
          window.mediaSoupHandler,
          handlerKind,
          deviceId
        );
        persistSelection(deviceId);
        await refreshMediaDevices();
        toast.success('Meeting input changed.');
      } catch (error) {
        persistSelection(previousDeviceId);
        console.warn('Meeting input switch failed:', error?.message || error);
        toast.error(
          error?.message ||
            'That input device is unavailable. The previous device is still active.'
        );
        await refreshMediaDevices();
      }
      return;
    }

    try {
      await verifyAudioOutputDevice(deviceId);
      localStorage.removeItem('meeting.nativeAudioOutputId');
      persistSelection(deviceId);
      window.dispatchEvent(new CustomEvent('meeting-output-device-change', {
        detail: { deviceId },
      }));
      toast.success('Meeting device changed.');
    } catch (error) {
      persistSelection(previousDeviceId);
      toast.error(
        error.message ||
          'Unable to use that speaker. Use “Choose speaker/headset” to grant access.'
      );
      await refreshMediaDevices();
    }
    } catch (error) {
      if (kind === 'audiooutput') {
        if (previousDeviceId.startsWith('native:')) {
          localStorage.setItem(
            'meeting.nativeAudioOutputId',
            previousDeviceId
          );
          localStorage.removeItem('meeting.audioOutputId');
        } else {
          localStorage.removeItem('meeting.nativeAudioOutputId');
          if (previousDeviceId) {
            localStorage.setItem('meeting.audioOutputId', previousDeviceId);
          } else {
            localStorage.removeItem('meeting.audioOutputId');
          }
        }
        setSelectedDevices((previous) => ({
          ...previous,
          audiooutput: previousDeviceId,
        }));
      }
      toast.error(error?.message || 'Unable to change that meeting device.');
    } finally {
      setDeviceSwitchBusy('');
    }
  };

  const chooseAudioOutput = async () => {
    if (!supportsOutputChooser || !supportsOutputSelection) return;
    const previousDeviceId =
      localStorage.getItem('meeting.audioOutputId') || '';
    setSpeakerChoiceBusy(true);
    try {
      // This must be called directly from the button gesture. Calling the
      // chooser only after another awaited operation causes Chromium to reject
      // it with InvalidStateError.
      const selected = await navigator.mediaDevices.selectAudioOutput();
      const resolvedDeviceId = String(selected?.deviceId || '');
      if (!resolvedDeviceId) {
        throw new Error('No speaker was selected.');
      }
      await verifyAudioOutputDevice(resolvedDeviceId);
      localStorage.setItem('meeting.audioOutputId', resolvedDeviceId);
      localStorage.removeItem('meeting.nativeAudioOutputId');
      window.dispatchEvent(new CustomEvent('meeting-output-device-change', {
        detail: { deviceId: resolvedDeviceId },
      }));

      await refreshMediaDevices();
      setMediaDevices((previous) => {
        if (
          previous.audiooutput.some(
            (device) => String(device.deviceId) === resolvedDeviceId
          )
        ) {
          return previous;
        }
        return {
          ...previous,
          audiooutput: [...previous.audiooutput, selected],
        };
      });
      setSelectedDevices((previous) => ({
        ...previous,
        audiooutput: resolvedDeviceId,
      }));
      setDeviceScanMessage(
        `Speaker selected: ${selected.label || 'Bluetooth / external speaker'}.`
      );
      toast.success('Meeting speaker changed.');
    } catch (error) {
      if (previousDeviceId) {
        localStorage.setItem('meeting.audioOutputId', previousDeviceId);
      } else {
        localStorage.removeItem('meeting.audioOutputId');
      }
      setSelectedDevices((previous) => ({
        ...previous,
        audiooutput: previousDeviceId,
      }));
      if (error?.name !== 'NotAllowedError') {
        toast.error(
          error?.message ||
            'Speaker selection failed. Check the browser and system sound settings.'
        );
      }
      setDeviceScanMessage(
        error?.name === 'NotAllowedError'
          ? 'Speaker selection was cancelled. The previous speaker is still active.'
          : error?.message ||
              'Speaker selection failed. The previous speaker is still active.'
      );
    } finally {
      setSpeakerChoiceBusy(false);
    }
  };

  const handleAudioPanelToggle = () => {
    if (showAudioPanel) {
      setShowAudioPanel(false);
      return;
    }
    setShowVideoQualityPanel(false);
    setShowAudioPanel(true);
    // The click is an explicit user action, so a join-muted participant can
    // grant microphone permission without being unmuted or publishing a track.
    void refreshMediaDevices({ requestAudioPermission: true });
  };

  useEffect(() => {
    const updateQualityState = (event) => {
      const detail = event?.detail || {};
      if (detail.mode) setVideoQualityModeState(detail.mode);
      setAppliedVideoProfile(detail.appliedProfile || '');
      if (detail.error) setVideoQualityMessage(detail.error);
    };
    window.addEventListener(
      'meeting-video-quality-change',
      updateQualityState
    );
    const status = window.mediaSoupHandler?.getVideoQualityStatus?.();
    if (status) updateQualityState({ detail: status });
    return () =>
      window.removeEventListener(
        'meeting-video-quality-change',
        updateQualityState
      );
  }, []);

  useEffect(() => {
    const updateAudioModeState = (event) => {
      const detail = event?.detail || {};
      if (detail.mode) setAudioModeState(detail.mode);
      if (detail.error) setAudioModeMessage(detail.error);
      else if (detail.gainUnavailable) {
        setAudioModeMessage('Natural microphone capture is active. Extra volume boost could not resume; adjust volume again if needed.');
      }
    };
    window.addEventListener(
      'meeting-audio-mode-change',
      updateAudioModeState
    );
    const status = window.mediaSoupHandler?.getAudioModeStatus?.();
    if (status) updateAudioModeState({ detail: status });
    return () =>
      window.removeEventListener(
        'meeting-audio-mode-change',
        updateAudioModeState
      );
  }, []);

  const chooseAudioMode = async (mode) => {
    if (audioModeBusy || mode === audioMode) return;
    const previousMode = audioMode;
    setAudioModeBusy(true);
    setAudioModeMessage('');
    try {
      const handler = window.mediaSoupHandler;
      if (!handler?.setAudioMode) {
        persistMeetingAudioMode(mode);
        setAudioModeState(mode);
        setAudioModeMessage(
          'Saved. This mode will apply when your microphone connects.'
        );
        return;
      }
      const status = await handler.setAudioMode(mode);
      const appliedMode = status?.mode || mode;
      setAudioModeState(appliedMode);
      setAudioModeMessage(
        appliedMode === MEETING_AUDIO_MODES.WORSHIP
          ? 'Music capture is active. Use wired headphones and one lead/accompaniment source for the tightest timing.'
          : 'Voice processing is active for clear speech and echo control.'
      );
    } catch (error) {
      setAudioModeState(previousMode);
      setAudioModeMessage(
        error?.message || 'Unable to change the microphone audio mode.'
      );
    } finally {
      setAudioModeBusy(false);
    }
  };

  const chooseVideoQuality = async (mode) => {
    if (videoQualityBusy) return;
    const previousMode = videoQualityMode;
    setVideoQualityBusy(true);
    setVideoQualityMessage('');
    try {
      const handler = window.mediaSoupHandler;
      if (!handler?.setVideoQualityMode) {
        throw new Error('Camera quality controls are still connecting.');
      }
      const status = await handler.setVideoQualityMode(mode);
      setVideoQualityModeState(status?.mode || mode);
      setAppliedVideoProfile(status?.appliedProfile || '');
      setVideoQualityMessage(
        status?.pending
          ? 'Saved. This quality will apply when the camera turns on.'
          : mode === 'auto'
            ? 'Auto will adapt gradually using bandwidth, loss, latency and CPU.'
            : 'Applied live without leaving or reconnecting the meeting.'
      );
    } catch (error) {
      setVideoQualityModeState(previousMode);
      setVideoQualityMessage(
        error?.message || 'Unable to change camera quality.'
      );
    } finally {
      setVideoQualityBusy(false);
    }
  };

  const handleVideoQualityPanelToggle = () => {
    setShowAudioPanel(false);
    setShowVideoQualityPanel((visible) => !visible);
    const status = window.mediaSoupHandler?.getVideoQualityStatus?.();
    if (status) {
      setVideoQualityModeState(status.mode);
      setAppliedVideoProfile(status.appliedProfile || '');
    }
  };

  const onMicGainChange = (event) => {
    const nextGain = clampMicGain(event.target.value);
    const previousGain = micGain;
    setMicGainValue(nextGain);
    setMicGainError('');

    const handler = window.mediaSoupHandler;
    if (!handler?.setMicGain) {
      setMicGainError('Microphone volume is not ready yet.');
      setMicGainValue(previousGain);
      return;
    }
    void handler.setMicGain(nextGain).catch((error) => {
      setMicGainValue(previousGain);
      localStorage.setItem('meeting.micGain', String(previousGain));
      setMicGainError(
        error?.message || 'Unable to adjust microphone volume.'
      );
    });
  };

  const handleMoreClick = () => {
    setShowTray((v) => !v);
    if (typeof showMore === 'function') showMore();
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (typeof window.__defaultOutputVolume !== 'number') window.__defaultOutputVolume = 1.0;
      if (typeof window.__playbackBoost !== 'number') window.__playbackBoost = 1.0;
      if (!window.__emitAudioSettingsChange) {
        window.__emitAudioSettingsChange = () => window.dispatchEvent(new CustomEvent('audio-settings-change'));
      }
    }
  }, []);

  const [speakerVol, setSpeakerVol] = useState(
    typeof window !== 'undefined' && typeof window.__defaultOutputVolume === 'number' ? window.__defaultOutputVolume : 1.0
  );

  const onSpeakerVolChange = (e) => {
    const v = Math.max(0, Math.min(1, Number(e.target.value)));
    setSpeakerVol(v);
    window.__defaultOutputVolume = v;
    window.__emitAudioSettingsChange?.();
  };

  const commonBtnBase = "shrink-0 min-h-[44px] min-w-[52px] md:min-w-[70px] p-2 md:p-3 rounded-full flex flex-col items-center";
  const labelCls = 'block whitespace-nowrap text-[10px] md:text-xs mt-1 text-white';

  const handleVideoToggle = async () => {
    if (isExam && String(authUser?.role).toLowerCase() === 'student' && videoEnabled) return;
    if (mediaToggleBusy) return;
    const newVideoStatus = !videoEnabled;
    setMediaToggleBusy('video');
    try {
      if (!window.mediaSoupHandler?.toggleVideo) {
        throw new Error('Camera controls are still connecting. Please try again.');
      }
      // MediaSoupHandler applies the track change immediately. Its single
      // onLocalMediaStateChange callback updates Redux and sends one
      // acknowledged combined media-state sync.
      await window.mediaSoupHandler.toggleVideo(newVideoStatus);
    } catch (error) {
      toast.error(
        error?.message ||
        `Unable to turn the camera ${newVideoStatus ? 'on' : 'off'}`
      );
    } finally {
      setMediaToggleBusy('');
    }
  };

  const handleAudioToggle = async () => {
    if (mediaToggleBusy) return;
    const newAudioStatus = !audioEnabled;
    setMediaToggleBusy('audio');
    try {
      if (!window.mediaSoupHandler?.toggleAudio) {
        throw new Error('Microphone controls are still connecting. Please try again.');
      }
      await window.mediaSoupHandler.toggleAudio(newAudioStatus);
    } catch (error) {
      toast.error(
        error?.message ||
        `Unable to turn the microphone ${newAudioStatus ? 'on' : 'off'}`
      );
    } finally {
      setMediaToggleBusy('');
    }
  };

  const forceStopMedia = () => {
    const currentStream = store.getState().media?.localStream;
    if (currentStream) {
      currentStream.getTracks().forEach(t => { t.enabled = false; t.stop(); });
    }
    if (window._rawStreamForBlur) {
      window._rawStreamForBlur.getTracks().forEach(t => { t.enabled = false; t.stop(); });
      window._rawStreamForBlur = null;
    }
    if (window.mediaSoupHandler) {
      window.mediaSoupHandler.localStream?.getTracks().forEach(t => { t.enabled = false; t.stop(); });
      window.mediaSoupHandler.destroy();
      window.mediaSoupHandler = null;
    }
    document.querySelectorAll('video, audio').forEach(media => {
      if (media.srcObject) {
        media.srcObject.getTracks().forEach(t => t.stop());
        media.srcObject = null;
      }
    });
  };

  const handleEndCall = async () => {
    const roomId = currentRoom?.roomId;
    const sessionId = getStoredMeetingSessionId();
    const userId = getStoredMeetingUserId();

    if (!roomId || !sessionId || !userId) {
      toast.error('Missing required meeting information.');
      return;
    }

    if (canEndMeeting) {
      dispatch(
        showConfirm({
          message: 'Are you sure you want to end the meeting for all participants?',
          onConfirm: async () => {
            const toastId = toast.loading('Ending meeting...');

            try {
              await flushClassBoards(roomId);
              if ((recording && canRecord) || meetingRecorder.isSavingForRoom?.(roomId)) {
                try {
                  const stopResult = await stopRoomRecording();
                  if (!stopResult?.requested) dispatch(toggleRecording(false));
                } catch (error) {
                  // End the class once the final capture is durable locally.
                  // The authenticated app keeps saving it on Meeting Ended.
                  const pending = await pendingDirectRecordings(roomId);
                  const saved = pending.find(row => row.id === stoppedRecordingIdRef.current);
                  if (!saved?.captureEnded || !saved.totalBytes || saved.captureIncomplete) throw error;
                  dispatch(toggleRecording(false));
                }
              }
              let response;

              try {
                response = await emitWithAck(
                  socket,
                  'end-meeting',
                  { roomId },
                  5000
                );

                if (!response?.success) {
                  throw new Error(response?.error || 'Socket end failed');
                }
              } catch (socketError) {
                // Production fallback: ending the meeting must still work when
                // the Socket.IO acknowledgement is lost during a network flap.
                await api.post(`/rooms/${encodeURIComponent(roomId)}/end`);
                response = { success: true, fallback: 'rest' };
              }

              forceStopMedia();
              clearMeetingRuntimeStorage({ keepAuth: true });
              navigate('/meeting-ended', {
                replace: true,
                state: { roomId, endedAt: new Date().toISOString() },
              });
            } catch (error) {
              toast.error(error.message || 'Failed to end meeting');
            } finally {
              toast.dismiss(toastId);
            }
          },
        })
      );
      return;
    }

    dispatch(
      showConfirm({
        message: 'Are you sure you want to leave the meeting?',
        onConfirm: async () => {
          try { await flushClassBoards(roomId); }
          catch { toast.error('Your board has not saved. Please retry before leaving.'); return; }
          try {
            // Preserve the owner's last chunk before marking them absent or
            // destroying the audio/video sources used by the recorder.
            if (meetingRecorder.isOwnerForRoom(roomId) || meetingRecorder.isSavingForRoom?.(roomId)) {
              await meetingRecorder.stopIfActive(roomId);
              dispatch(toggleRecording(false));
            }
          } catch (error) {
            toast.error(recordingUiError(error));
            return;
          }
          let leaveSucceeded = false;

          try {
            const response = await emitWithAck(
              socket,
              'participant-left',
              { roomId, sessionId },
              2500
            );
            leaveSucceeded = response?.success === true;
          } catch {
            leaveSucceeded = false;
          }

          if (!leaveSucceeded) {
            // REST fallback ensures the DB is updated even when the socket was
            // lost immediately before the user clicked Leave.
            try {
              await api.put(
                `/rooms/${encodeURIComponent(roomId)}/participants/${encodeURIComponent(
                  sessionId
                )}/self-state`,
                { status: 'left' }
              );
              leaveSucceeded = true;
            } catch (error) {
              console.warn('Leave REST fallback failed:', error);
            }
          }

          forceStopMedia();
          navigate(`/left-meeting/${encodeURIComponent(roomId)}`, {
            replace: true,
            state: {
              sessionId,
              leftAt: new Date().toISOString(),
              leaveSaved: leaveSucceeded,
            },
          });
        },
      })
    );
  };


  const sendReaction = (reaction) => {
    if (!socket?.connected) {
      toast.warning('Reaction could not be sent while reconnecting.');
      return;
    }
    socket.emit(
      'send-reaction',
      { roomId: currentRoom.roomId, reaction },
      (response = {}) => {
        if (response.success === false) {
          toast.warning(response.error || 'Reaction could not be sent.');
        }
      }
    );
    setShowReactions(false);
  };

  const handleHandRaise = () => {
    const nextRaised = !handRaised;
    if (!socket?.connected) {
      toast.warning('Hand raise will be available after reconnection.');
      return;
    }

    // Optimistic local update; the authenticated socket acknowledgement rolls
    // it back if the participant is no longer admitted.
    dispatch(toggleHandRaised(nextRaised));
    socket.emit('handRaised', { isRaised: nextRaised }, (response = {}) => {
      if (response.success === false) {
        dispatch(toggleHandRaised(!nextRaised));
        toast.warning(response.error || 'Unable to update hand raise.');
      }
    });
  };

  const handleScreenShare = async () => {
    if (!canScreenShare || isScreenShareLoading) return;
    setIsScreenShareLoading(true);
    try {
      if (screenSharing) {
        await window.mediaSoupHandler.stopScreenSharing();
        dispatch(toggleScreenSharing(false));
        toast.success('Screen sharing stopped');
      } else {
        const started = await window.mediaSoupHandler.startScreenSharing();
        if (!started) return;
        dispatch(toggleScreenSharing(true));
        toast.success('Screen sharing started');
      }
    } finally {
      setIsScreenShareLoading(false);
    }
  };

  const handleRecording = async () => {
    if (!canRecord || isProcessing) return;
    const stopping = recording || recordingSaveRetry || meetingRecorder.isSavingForRoom?.(currentRoom.roomId);
    setIsProcessing(true);
    try {
      if (!stopping) {
        if (typeof currentRoom?.settings?.automaticRecordingEnabled === 'boolean' || currentRoom?.settings?.autoRecord === true) {
          window.dispatchEvent(new CustomEvent('meeting-recording-start-click', { detail: { roomId: currentRoom.roomId } }));
          return;
        }
        await meetingRecorder.unlockRecordingAudio();
        const result = await meetingRecorder.start({
          roomId: currentRoom.roomId,
          title: currentRoom.title,
          sessionId: getStoredMeetingSessionId(),
        });
        const id = result?.recordingId;
        if (!id) throw new Error('Failed to start recording');
        dispatch(toggleRecording(true));
        stoppedRecordingIdRef.current = '';
        toast.success('Recording started');
      } else {
        await stopRoomRecording();
        dispatch(toggleRecording(false));
        setRecordingSaveRetry(false);
        toast.success('Recording saved.');
      }
    } catch (err) {
      if (stopping) setRecordingSaveRetry(true);
      toast.error(recordingUiError(err));
    } finally {
      setIsProcessing(false);
      setRecordingSavePhase('');
    }
  };
  recordingActionRef.current = () => {
    if (recording || recordingSaveRetry || meetingRecorder.isSavingForRoom?.(currentRoom?.roomId)) return handleRecording();
    return undefined;
  };

  // ==================== SUBMIT INTERVIEW DECISION ====================
  const submitInterviewDecision = async (decision) => {
    const roomId = String(currentRoom?.roomId || '').trim();
    if (!roomId) throw new Error('Interview room ID is missing');
    const { data } = await api.post(
      `/interviews/${encodeURIComponent(roomId)}/decision`,
      { decision }
    );
    return data;
  };

  if (isExam) return <ExamMeetingControls room={currentRoom} role={authUser?.role || sessionStorage.getItem('userRole')} userId={authUser?._id || authUser?.id} socket={socket}
    stream={examStream} remoteStreams={remoteStreams} remoteVideoStatus={remoteVideoStatus} audioEnabled={audioEnabled} videoEnabled={videoEnabled}
    onAudio={handleAudioToggle} onMuteForAnswer={async()=>{
      const handler=window.mediaSoupHandler;
      if(!handler?.toggleAudio)throw new Error('Meeting microphone controls are reconnecting. Retry when connected.');
      handler.examDictationLocked=true;
      try {
        await handler.toggleAudio(false);
        const state=handler.getLocalMediaState?.('audio');
        const producer=handler.producers?.get?.('audio');
        if(state?.enabled===true || handler.localStream?.getAudioTracks?.().some(track=>track.enabled) || (producer&&!producer.closed&&!producer.paused))throw new Error('The meeting microphone could not be muted. Dictation has not started.');
      } catch(error){handler.examDictationLocked=false;throw error;}
    }} onCamera={handleVideoToggle} onShare={handleScreenShare} onLeave={handleEndCall}
    onChat={() => { dispatch(toggleChatStatus(!chatStatus)); onToggleChat?.(); }}
    onParticipants={() => { dispatch(toggleParticipantsStatus(!participantsStatus)); onToggleParticipants?.(); }} />;
  return (
    <div className="meeting-controls w-full relative" onClickCapture={trackStudentControl}>
      {showReactions && (
        <div className="meeting-control-popover meeting-reactions-popover reactoin-width absolute bottom-20 left-1/2 -translate-x-1/2 bg-[#3c4043] p-2 rounded-xl flex flex-wrap gap-2 max-w-xs md:max-w-sm lg:max-w-md overflow-y-auto max-h-48 z-50">
          {reactions.map((reaction) => {
            const isEmoji = /^[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+$/u.test(reaction);
            return (
              <button
                key={reaction}
                onClick={() => sendReaction(reaction)}
                className={`px-3 py-1 rounded-full hover:scale-110 hover:bg-gray-500 transition transform text-white whitespace-nowrap ${
                  isEmoji ? 'text-xl md:text-2xl bg-gray-600' : 'text-sm md:text-base bg-gray-700'
                }`}
              >
                {reaction}
              </button>
            );
          })}
        </div>
      )}

      <div className="meeting-control-strip w-full overflow-x-auto" tabIndex={0} role="region" aria-label="Meeting controls, scroll to more actions">
        <div className="mx-auto flex flex-nowrap items-center gap-2 px-2 py-2 justify-start md:justify-center">
          {/* Mic Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleAudioToggle}
                disabled={Boolean(mediaToggleBusy)}
                aria-pressed={audioEnabled}
                aria-label={audioEnabled ? 'Microphone on. Mute microphone' : 'Muted. Unmute microphone'}
                title={audioEnabled ? 'Mic on — click to mute' : 'Muted — click to unmute'}
                className={`${commonBtnBase} ${audioEnabled ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {mediaToggleBusy === 'audio'
                  ? <FaSpinner size={18} className="animate-spin text-white" />
                  : audioEnabled
                    ? <FaMicrophone size={18} className="text-white" />
                    : <FaMicrophoneSlash size={18} className="text-white" />}
                <span className={labelCls}>{audioEnabled ? 'Mic on' : 'Muted'}</span>
              </button>
            </div>
          )}

          {/* Camera Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleVideoToggle}
                disabled={Boolean(mediaToggleBusy)}
                aria-pressed={videoEnabled}
                aria-label={videoEnabled ? 'Camera on. Turn camera off' : 'Camera off. Turn camera on'}
                title={videoEnabled ? 'Camera on — click to turn off' : 'Camera off — click to turn on'}
                className={`${commonBtnBase} ${videoEnabled ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {mediaToggleBusy === 'video'
                  ? <FaSpinner size={18} className="animate-spin text-white" />
                  : videoEnabled
                    ? <FaVideo size={18} className="text-white" />
                    : <FaVideoSlash size={18} className="text-white" />}
                <span className={labelCls}>{videoEnabled ? 'Camera on' : 'Camera off'}</span>
              </button>
            </div>
          )}

          {/* Screen Share Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleScreenShare}
                disabled={isScreenShareLoading}
                aria-busy={isScreenShareLoading}
                className={`${commonBtnBase} ${screenSharing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                {isScreenShareLoading
                  ? <FaSpinner size={18} className="animate-spin text-white" />
                  : <FaDesktop size={18} className="text-white" />}
                <span className={labelCls}>{screenSharing ? 'Stop Share' : 'Share'}</span>
              </button>
            </div>
          )}

          {/* Camera Quality Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleVideoQualityPanelToggle}
                aria-expanded={showVideoQualityPanel}
                className={`${commonBtnBase} ${
                  showVideoQualityPanel
                    ? 'bg-blue-600 hover:bg-blue-500'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <FaSignal size={18} className="text-white" />
                <span className={labelCls}>Quality</span>
              </button>
            </div>
          )}

          {/* Reactions Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button onClick={() => setShowReactions((v) => !v)} className={`${commonBtnBase} bg-gray-700 hover:bg-gray-600`}>
                <FaRegGrinAlt size={18} className="text-white" />
                <span className={labelCls}>Reaction</span>
              </button>
            </div>
          )}

          {/* Hand Raise Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleHandRaise}
                className={`${commonBtnBase} ${handRaised ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-gray-700 hover:bg-gray-600'}`}
                aria-label={handRaised ? 'Lower hand' : 'Raise hand'}
              >
                <FaHandPaper size={18} className="text-white" />
                <span className={labelCls}>Hand</span>
              </button>
            </div>
          )}

          {/* Audio Settings Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button onClick={handleAudioPanelToggle} className={`${commonBtnBase} bg-gray-700 hover:bg-gray-600`}>
                <FaVolumeUp size={18} className="text-white" />
                <span className={labelCls}>Audio</span>
              </button>
            </div>
          )}

          {/* Chat Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={() => {
                  dispatch(toggleChatStatus(!chatStatus));
                  onToggleChat && onToggleChat();
                }}
                className={`${commonBtnBase} ${chatStatus ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-600'} ${
                  hasUnreadChat
                    ? 'ring-2 ring-amber-300 motion-safe:animate-pulse motion-reduce:animate-none'
                    : ''
                }`}
                aria-label={hasUnreadChat
                  ? `Open chat with ${unreadMessages || 1} new message${(unreadMessages || 1) === 1 ? '' : 's'}`
                  : 'Open meeting chat'}
              >
                <FaComments size={18} className="text-white" />
                <span className={labelCls}>{hasUnreadChat ? 'New Chat' : 'Chat'}</span>
                {unreadMessages > 0 && (
                  <span
                    className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white motion-safe:animate-bounce motion-reduce:animate-none"
                    role="status"
                    aria-live="polite"
                  >
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Participants Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={() => {
                  dispatch(toggleParticipantsStatus(!participantsStatus));
                  onToggleParticipants && onToggleParticipants();
                }}
                className={`${commonBtnBase} ${participantsStatus ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-700 hover:bg-gray-600'}`}
              >
                <FaUsers size={18} className="text-white" />
                <span className={labelCls}>Participants</span>
              </button>
            </div>
          )}

          {/* Key-free World English Bible */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                type="button"
                onClick={() => onToggleBible?.()}
                className={`${commonBtnBase} ${
                  bibleOpen
                    ? 'bg-amber-500 hover:bg-amber-400'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                aria-label={bibleOpen ? 'Close Bible' : 'Open Bible'}
                aria-pressed={bibleOpen}
              >
                <FaBible size={18} className={bibleOpen ? 'text-slate-950' : 'text-white'} />
                <span className={`${labelCls} ${bibleOpen ? '!text-slate-950' : ''}`}>
                  Bible
                </span>
              </button>
            </div>
          )}

          {/* More Options Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group" ref={moreMenuRef}>
              <button onClick={handleMoreClick} className={`${commonBtnBase} bg-gray-700 hover:bg-gray-600`}>
                <FaEllipsisH size={18} className="text-white" />
                <span className={labelCls}>More</span>
              </button>
            </div>
          )}

          {/* Meeting Notes Button */}
          <div className="relative flex flex-col items-center group">
            <button onClick={() => setShowNotesModal(true)} className={`${commonBtnBase} bg-gray-700 hover:bg-gray-600`}>
              <FaFileSignature size={18} className="text-white" />
              <span className={labelCls}>Notes</span>
            </button>
          </div>

          {/* Interview Decision Button (Only for Host) */}
          {canDecideInterview && (
            <div className="relative flex flex-col items-center group">
              <button 
                onClick={() => {
                  // Get current meeting participant details - you can fetch from room state
                  setCurrentApplicant({ 
                    regNo: '',
                    name: currentRoom?.applicantName || currentRoom?.title || 'Applicant'
                  });
                  setShowDecisionModal(true);
                }} 
                className={`${commonBtnBase} bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600`}
              >
                <FaUserTie size={18} className="text-white" />
                <span className={labelCls}>Decision</span>
              </button>
            </div>
          )}

          {/* Stop remains available for every authorized active capture, including automatic recording. */}
          {canRecord && (
            <div className="relative flex flex-col items-center group">
              <button
                type="button"
                onClick={handleRecording}
                className={`${commonBtnBase} ${recording ? 'bg-red-600 hover:bg-red-700 recording-active' : 'bg-gray-700 hover:bg-gray-600'}`}
                disabled={isProcessing}
                aria-busy={isProcessing}
                aria-label={isProcessing ? 'Saving recording' : recordingSaveRetry ? 'Retry recording save' : recording ? 'Stop recording' : 'Start recording'}
              >
                {isProcessing ? <FaSpinner size={18} className="animate-spin text-white" /> : recording || recordingSaveRetry ? <FaStop size={18} className="text-white" /> : <FaCircle size={18} className="text-red-500" />}
                <span className={labelCls}>{isProcessing ? recordingSavePhase === 'stopping' ? 'Stopping…' : 'Saving…' : recordingSaveRetry ? 'Retry save' : recording ? 'Stop' : 'Record'}</span>
              </button>
            </div>
          )}

          {/* Every admitted classroom participant can open/restore the shared board. */}
          {canOpenBlackboard && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={() => onOpenBlackboard?.()}
                className={`${commonBtnBase} bg-indigo-700 hover:bg-indigo-600`}
              >
                <FaChalkboardTeacher size={18} className="text-white" />
                <span className={labelCls}>Board</span>
              </button>
            </div>
          )}

          {/* Meeting Info Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={() => {
                  dispatch(toggleMeetInfo(!showInfoStatus));
                  onToggleMeetingInfo && onToggleMeetingInfo();
                }}
                className={`${commonBtnBase} ${showInfoStatus ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-600'} text-white`}
              >
                <FaInfoCircle size={18} className="text-white" />
                <span className={labelCls}>Info</span>
              </button>
            </div>
          )}

          {/* End Call Button */}
          {statusget !== 'waiting' && (
            <div className="relative flex flex-col items-center group">
              <button
                onClick={handleEndCall}
                className={`${commonBtnBase} bg-red-600 hover:bg-red-700`}
              >
                <FaPhoneSlash size={18} className="text-white" />
                <span className={labelCls}>{canEndMeeting ? 'End' : 'Leave'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Audio Panel Popup */}
 {showAudioPanel && (
        <div
          className="meeting-control-popover meeting-audio-popover absolute bottom-20 left-1/2 -translate-x-1/2 z-[100] w-[95vw] md:w-[min(92vw,400px)] 
             max-h-[75vh] overflow-y-auto rounded-3xl bg-[#0b0d10]/95 border border-white/10 
             backdrop-blur-2xl shadow-2xl p-4 md:p-5"
        >
          <div className="sticky top-0 bg-[#0b0d10]/95 z-10 flex items-center justify-between mb-3 md:mb-4 pb-2 border-b border-white/5">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">🎚️</span>
              <h3 className="text-white/95 font-semibold tracking-wide">Meeting devices &amp; audio</h3>
            </div>
            <button
              onClick={() => setShowAudioPanel(false)}
              className="px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm font-bold"
            >
              Close
            </button>
          </div>

          <section
            className="mb-4 rounded-2xl border border-violet-300/20 bg-violet-500/10 p-3"
            aria-labelledby="meeting-audio-mode-title"
          >
            <div className="mb-2">
              <h4
                id="meeting-audio-mode-title"
                className="text-sm font-semibold text-white"
              >
                Microphone mode
              </h4>
              <p className="mt-1 text-[11px] leading-4 text-violet-100/80">
                Voice keeps the existing meeting sound. Worship preserves
                singing and instruments with speech filters disabled.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                aria-pressed={audioMode === MEETING_AUDIO_MODES.VOICE}
                disabled={audioModeBusy}
                onClick={() => void chooseAudioMode(MEETING_AUDIO_MODES.VOICE)}
                className={`rounded-xl border px-3 py-2 text-left text-xs transition disabled:cursor-wait disabled:opacity-60 ${
                  audioMode === MEETING_AUDIO_MODES.VOICE
                    ? 'border-sky-300 bg-sky-500/25 text-white'
                    : 'border-white/10 bg-black/20 text-gray-300 hover:bg-white/10'
                }`}
              >
                <span className="block font-bold">🎙️ Voice</span>
                <span className="mt-1 block text-[10px] opacity-80">
                  Speech + echo control
                </span>
              </button>
              <button
                type="button"
                aria-pressed={audioMode === MEETING_AUDIO_MODES.WORSHIP}
                disabled={audioModeBusy}
                onClick={() => void chooseAudioMode(MEETING_AUDIO_MODES.WORSHIP)}
                className={`rounded-xl border px-3 py-2 text-left text-xs transition disabled:cursor-wait disabled:opacity-60 ${
                  audioMode === MEETING_AUDIO_MODES.WORSHIP
                    ? 'border-fuchsia-300 bg-fuchsia-500/25 text-white'
                    : 'border-white/10 bg-black/20 text-gray-300 hover:bg-white/10'
                }`}
              >
                <span className="block font-bold">🎵 Worship / Music</span>
                <span className="mt-1 block text-[10px] opacity-80">
                  Music range + low latency
                </span>
              </button>
            </div>
            {audioModeBusy && (
              <p className="mt-2 text-xs text-violet-100" role="status">
                Switching the live microphone safely…
              </p>
            )}
            {audioModeMessage && !audioModeBusy && (
              <p
                className="mt-2 rounded-lg bg-black/20 px-2.5 py-2 text-xs leading-5 text-violet-50"
                role="status"
                aria-live="polite"
              >
                {audioModeMessage}
              </p>
            )}
            <p className="mt-2 text-[11px] leading-4 text-amber-200">
              Worship mode: wired headphones are strongly recommended. Internet
              travel time cannot be removed; for tight group singing, use one
              lead/accompaniment feed while others sing locally with microphones muted.
            </p>
          </section>

          <div className="mb-3 rounded-xl border border-sky-300/20 bg-sky-500/10 p-3">
            <div className="mb-2 flex items-center justify-between text-xs text-sky-100">
              <span className="font-semibold">Live microphone level</span>
              <span>{audioEnabled ? 'Speak to check your input' : 'Muted — meeting audio stays off'}</span>
            </div>
            <div role="meter" aria-label="Live microphone level" aria-valuemin={0}
              aria-valuemax={100} aria-valuenow={Math.round(microphoneLevel * 100)}
              className="h-2 overflow-hidden rounded-full bg-black/30">
              <div className="h-full rounded-full bg-gradient-to-r from-sky-400 via-emerald-300 to-amber-300 transition-[width] duration-75"
                style={{ width: `${Math.round(microphoneLevel * 100)}%` }} />
            </div>
            <p className="mt-2 text-[11px] leading-4 text-sky-100/75">
              The meter reads the microphone currently used by this meeting. If it stays still while unmuted, check the selected input and browser microphone permission.
            </p>
          </div>

          <div className="space-y-3 rounded-xl bg-white/5 p-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() =>
                  void refreshMediaDevices({
                    requestAudioPermission: true,
                  })
                }
                disabled={deviceScanBusy || speakerChoiceBusy}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600/80 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:cursor-wait disabled:opacity-60"
              >
                {deviceScanBusy && (
                  <FaSpinner aria-hidden className="animate-spin" />
                )}
                Refresh devices
              </button>
              {supportsOutputChooser && supportsOutputSelection && (
                <button
                  type="button"
                  onClick={() => void chooseAudioOutput()}
                  disabled={speakerChoiceBusy || deviceScanBusy}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600/80 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:cursor-wait disabled:opacity-60"
                >
                  {speakerChoiceBusy && (
                    <FaSpinner aria-hidden className="animate-spin" />
                  )}
                  Choose speaker/headset
                </button>
              )}
            </div>
            {deviceScanMessage && (
              <p
                className="rounded-lg bg-black/20 px-2.5 py-2 text-xs leading-5 text-sky-100"
                role="status"
                aria-live="polite"
              >
                {deviceScanMessage}
              </p>
            )}
            {[
              ['audioinput', 'Microphone'],
              ['videoinput', 'Camera'],
              ['audiooutput', 'Speaker'],
            ].map(([kind, label]) => (
              <label key={kind} className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-300">
                  {label}
                </span>
                <select
                  value={selectedDevices[kind]}
                  onChange={(event) => void selectMediaDevice(kind, event.target.value)}
                  disabled={
                    Boolean(deviceSwitchBusy) ||
                    (kind === 'audiooutput' &&
                      !supportsOutputSelection &&
                      !supportsNativeOutputSelection)
                  }
                  className="w-full rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">System default</option>
                  {selectedDevices[kind] &&
                    !mediaDevices[kind].some(
                      (device) =>
                        String(device.deviceId) === selectedDevices[kind]
                    ) && (
                      <option value={selectedDevices[kind]} disabled>
                        Saved {label.toLowerCase()} (unavailable)
                      </option>
                    )}
                  {mediaDevices[kind].map((device, index) => (
                    <option key={device.deviceId || `${kind}-${index}`} value={device.deviceId}>
                      {device.label || `${label} ${index + 1}`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            {!supportsOutputSelection && !supportsNativeOutputSelection && (
              <p className="text-xs text-amber-300">
                Speaker/Bluetooth output is controlled by your device’s system sound or media-output settings.
              </p>
            )}
            {!supportsOutputSelection && supportsNativeOutputSelection && (
              <p className="text-xs text-emerald-300">
                Android native routing is active. Paired Bluetooth, USB,
                earpiece and speaker devices appear above.
              </p>
            )}
            {supportsOutputSelection && !supportsOutputChooser && (
              <p className="text-xs text-amber-300">
                Connect the speaker or headset in system sound settings, then press Refresh devices.
              </p>
            )}
            <p className="text-xs leading-5 text-gray-400">
              {audioMode === MEETING_AUDIO_MODES.WORSHIP
                ? 'Worship mode requests unprocessed 48 kHz music capture, stereo when supported, Opus FEC and no silence suppression.'
                : 'Echo cancellation, background-noise suppression and automatic gain are requested from the selected microphone; Opus error recovery is enabled.'}
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-4">
            <div className="rounded-xl bg-white/5 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm text-gray-200">Microphone Volume</span>
                <span className="text-xs text-gray-300">
                  {Math.round(micGain * 100)}%
                </span>
              </div>
              <input
                type="range"
                min="0.25"
                max="2"
                step="0.05"
                value={micGain}
                onChange={onMicGainChange}
                disabled={!micGainSupported}
                aria-label="Outgoing microphone volume"
                className="pro-slider h-2 w-full appearance-none rounded-full bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundImage: `linear-gradient(currentColor, currentColor), linear-gradient(#ffffff22,#ffffff22)`,
                  backgroundSize: `${Math.round(((micGain - 0.25) / 1.75) * 100)}% 100%, 100% 100%`,
                  backgroundRepeat: 'no-repeat',
                  color: '#60a5fa',
                }}
              />
              <p className={`mt-2 text-xs ${
                micGainError || !micGainSupported
                  ? 'text-amber-300'
                  : 'text-gray-400'
              }`}>
                {micGainError ||
                  (!micGainSupported
                    ? 'This browser does not support outgoing microphone volume control.'
                    : 'Adjusts the volume other participants receive; 100% keeps the natural level.')}
              </p>
            </div>
            <div className="rounded-xl bg-white/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-200">Speaker Volume</span>
                <span className="text-xs text-gray-300">{Math.round(speakerVol * 100)}%</span>
              </div>
              <input
                type="range" min="0" max="1" step="0.01"
                value={speakerVol}
                onChange={onSpeakerVolChange}
                className="pro-slider w-full h-2 appearance-none rounded-full bg-white/10"
                style={{
                  backgroundImage: `linear-gradient(currentColor, currentColor), linear-gradient(#ffffff22,#ffffff22)`,
                  backgroundSize: `${Math.round(speakerVol * 100)}% 100%, 100% 100%`,
                  backgroundRepeat: 'no-repeat',
                  color: '#34d399'
                }}
              />
            </div>
            {/* Loud Speaker Button code... */}
          </div>
        </div>
      )}

      {/* Live Camera Quality Popup */}
      {showVideoQualityPanel && (
        <div className="meeting-control-popover meeting-video-popover absolute bottom-20 left-1/2 z-[105] w-[min(94vw,360px)] -translate-x-1/2 rounded-2xl border border-white/10 bg-[#0b0d10]/95 p-4 shadow-2xl backdrop-blur-2xl">
          <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2">
            <div>
              <h3 className="font-semibold text-white">Video quality</h3>
              <p className="text-[11px] text-gray-400">
                Changes live—no rejoin or transport restart
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowVideoQualityPanel(false)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/20"
            >
              Close
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {VIDEO_QUALITY_OPTIONS.map((option) => {
              const selected = videoQualityMode === option.key;
              return (
                <button
                  type="button"
                  key={option.key}
                  disabled={videoQualityBusy}
                  aria-pressed={selected}
                  onClick={() => void chooseVideoQuality(option.key)}
                  className={`rounded-xl border px-3 py-2 text-left transition disabled:cursor-wait disabled:opacity-60 ${
                    selected
                      ? 'border-blue-400 bg-blue-600/40 text-white'
                      : 'border-white/10 bg-white/5 text-gray-200 hover:bg-white/10'
                  }`}
                >
                  <span className="block text-sm font-semibold">
                    {option.label}
                  </span>
                  {option.width && (
                    <span className="block text-[10px] text-gray-400">
                      {option.width}×{option.height} · {option.frameRate} fps
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p
            role="status"
            aria-live="polite"
            className={`mt-3 rounded-lg px-3 py-2 text-xs ${
              videoQualityMessage
                ? 'bg-white/5 text-sky-100'
                : 'text-gray-400'
            }`}
          >
            {videoQualityMessage ||
              `Selected: ${videoQualityMode}${
                appliedVideoProfile
                  ? ` · sending ${appliedVideoProfile}`
                  : ''
              }`}
          </p>
        </div>
      )}

      {/* More Options Tray */}
      <AnimatePresence>
        {showTray && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="w-full px-2"
          >
            <div className="mt-2 bg-[#2f3135] rounded-2xl p-2 flex flex-wrap gap-2 justify-center">
              {canRecord && <>
                <button type="button" className="rounded-lg px-3 py-2 bg-gray-700 text-white" onClick={() => window.dispatchEvent(new CustomEvent('meeting-recording-uploads-open'))}>Recording uploads</button>
                <button type="button" className="rounded-lg px-3 py-2 bg-gray-700 text-white" onClick={() => window.dispatchEvent(new CustomEvent('meeting-recording-options-open'))}>Recording options</button>
              </>}
              {statusget !== 'waiting' && (
                <button className="min-w-[56px] md:min-w-[70px] p-2 md:p-3 rounded-full bg-gray-700 hover:bg-gray-600 flex flex-col items-center">
                  <FaClosedCaptioning size={18} className="text-white" />
                  <span className="hidden sm:block text-[11px] md:text-xs mt-1 text-white">CC</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interview Decision Modal */}
      <InterviewDecisionModal
        isOpen={showDecisionModal}
        onClose={() => setShowDecisionModal(false)}
        studentRegNo={currentApplicant.regNo}
        studentName={currentApplicant.name}
        onSubmit={submitInterviewDecision}
      />

      {/* Stylish Notes Modal */}
      <StylishNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        roomId={currentRoom?.roomId}
        roomTitle={currentRoom?.title}
      />
    </div>
  );
};

export default Controls;
